import type { CustomerStory } from '../../types/story'

const IMG = 'https://cdn.magicpatterns.com/patterns/generated-images/'

export const retailStories: CustomerStory[] = [
  {
    slug: 'all-in-one-supermarket',
    business: 'All-in-one supermarket',
    owner: 'Jemima Nyirasafari',
    role: 'Owner',
    industry: 'retail',
    location: 'Kicukiro, Kigali',
    image: `${IMG}26e15333-45ec-4421-ac04-24cf98518008.jpg`,
    alt: 'Jemima Nyirasafari standing at the counter of her mini supermarket',
    products: ['sales-pos', 'inventory', 'purchases', 'reports'],
    plan: 'Starter',
    staff: '4 staff',
    since: '2023',
    headline: 'How a Kigali mini supermarket took control of its stock',
    result: '[X]% fewer stock-outs',
    summary: 'Jemima replaced her stock notebook with live alerts and now reorders before shelves run empty.',
    quote: 'With Kayko, managing my inventory and tracking my sales has become second nature.',
    results: [
      { value: '[X]%', label: 'less stock waste' },
      { value: '[X] hrs', label: 'saved each week' },
      { value: '[X]×', label: 'faster checkout' },
    ],
    challenge: [
      'Jemima opened her mini supermarket in Kicukiro with a notebook for stock and a drawer for receipts. As the shop grew to more than 300 products, the notebook couldn’t keep up.',
      'Customers would ask for sugar or cooking oil only to find the shelf empty. At the end of each day, counting cash and matching it to sales took over an hour — and rarely added up.',
    ],
    solution: [
      'Jemima started with Point of Sale on a tablet at the counter. Every sale now updates stock automatically, and every payment — mobile money, card, or cash — is recorded against its receipt.',
      'She set reorder levels for her best sellers. When a product runs low, Kayko alerts her, and she turns it into a purchase order for her supplier in a few taps.',
    ],
    outcome: [
      'Shelves stay stocked on the products customers come back for, and the evening cash-up takes minutes instead of an hour.',
      'With profit reports by product, Jemima now knows which items actually make money — and has started stocking more of them.',
    ],
    inlineImage: {
      src: `${IMG}5c68bbb7-8987-4194-8f0e-9d3bfa95eafe.jpg`,
      alt: 'Jemima checking stock levels on shelves in her storeroom',
      caption: 'Jemima checks her storeroom against Kayko’s stock levels each morning.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Low-stock alerts · Kicukiro',
          badge: { label: '3 low', tone: 'warning' },
          rows: [
            { label: 'Sugar 1kg', value: '4 left', pct: 12, tone: 'negative' },
            { label: 'Cooking oil 1L', value: '9 left', pct: 24, tone: 'warning' },
            { label: 'Rice 5kg', value: '11 left', pct: 30, tone: 'warning' },
            { label: 'Maize flour 2kg', value: '52 left', pct: 86 },
          ],
          action: 'Create purchase order',
        },
      },
      caption: 'Low-stock alerts turned Jemima’s reordering from guesswork into a daily five-minute check.',
    },
    video: { duration: '2:14' },
  },
  {
    slug: 'jobs-clothing-shop',
    business: 'Job’s Clothing Shop',
    owner: 'Job Gashangaza',
    role: 'Owner',
    industry: 'retail',
    location: 'Nyarugenge, Kigali',
    image: `${IMG}1f9a33f4-6198-4f9d-89dd-0aca07eb18bb.jpg`,
    alt: 'Job Gashangaza in his clothing shop',
    products: ['sales-pos', 'expenses', 'reports'],
    plan: 'Starter',
    staff: '3 staff',
    since: '2024',
    headline: 'How a clothing shop found its real profit',
    result: '[X]% lower monthly costs',
    summary: 'Job started recording every cost and discovered where his margin was going.',
    quote: 'Kayko makes it easier to track my sales and expenses and plan confidently for growth.',
    results: [
      { value: '[X]%', label: 'lower monthly spending' },
      { value: '[X]', label: 'expenses recorded each month' },
      { value: '[X] hrs', label: 'saved on bookkeeping each week' },
    ],
    challenge: [
      'Sales at Job’s shop were strong, but the money at the end of the month never matched. Transport, airtime, and small repairs were paid from the drawer and never written down.',
    ],
    solution: [
      'Job now records every expense on his phone the moment he pays it, with a photo of the receipt. Sales and costs sit side by side in one profit report.',
    ],
    outcome: [
      'Within weeks he spotted delivery costs creeping up and changed suppliers. For the first time, he knows his profit every week — not just a feeling.',
    ],
    inlineImage: {
      src: `${IMG}c428e88a-0bce-4050-a006-e40f44c2650d.jpg`,
      alt: 'Recording an expense with a receipt photo',
      caption: 'Every receipt is photographed and attached to its expense.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Expenses · September',
          rows: [
            { label: 'Rent', value: 'RWF 350,000', pct: 45 },
            { label: 'Transport', value: 'RWF 148,000', pct: 19, tone: 'warning' },
            { label: 'Salaries', value: 'RWF 240,000', pct: 31 },
          ],
        },
      },
      caption: 'Expense categories showed Job exactly where his margin was going.',
    },
  },
  {
    slug: 'uwase-hardware',
    business: 'Uwase Hardware',
    owner: 'Claudine Uwase',
    role: 'Owner',
    industry: 'retail',
    location: 'Gikondo, Kigali',
    image: `${IMG}ecd9d923-5c1a-43ac-a833-0b82a75cd9c0.jpg`,
    alt: 'Claudine Uwase in her hardware store',
    products: ['reports', 'inventory', 'sales-pos'],
    plan: 'Growth',
    staff: '9 staff',
    since: '2023',
    headline: 'How a hardware store spotted a slow branch months early',
    result: '[X]% profit growth',
    summary: 'Branch reports showed Claudine which of her two stores needed attention.',
    quote: 'Now I check profit every evening, and I spotted a slow branch months earlier than I would have.',
    results: [
      { value: '[X]%', label: 'profit growth' },
      { value: '[X]', label: 'branches in one view' },
      { value: '[X] days', label: 'earlier to spot problems' },
    ],
    challenge: [
      'With two stores, Claudine relied on her accountant’s monthly summary. By the time problems showed up, a whole month had passed.',
    ],
    solution: [
      'Both stores now sell through Kayko, and consolidated reports show sales, profit, and stock side by side every day.',
    ],
    outcome: [
      'Claudine noticed the second branch was carrying the wrong mix of stock, rebalanced it, and brought its margin back in line.',
    ],
    inlineImage: {
      src: `${IMG}bf8c75d0-c0c3-451c-b8e2-c025c368382a.jpg`,
      alt: 'Reviewing branch reports on a laptop',
      caption: 'Claudine reviews both branches every evening.',
    },
    screenshot: { visual: { kind: 'screen', id: 'staff-sales' }, caption: 'Sales by branch and staff, updated live.' },
  },
  {
    slug: 'ingabire-fashion-house',
    business: 'Ingabire Fashion House',
    owner: 'Solange Ingabire',
    role: 'Owner',
    industry: 'retail',
    location: 'Remera, Kigali',
    image: `${IMG}55a948a8-f1a2-4b85-9533-52b1fa01fe2f.jpg`,
    alt: 'Solange Ingabire reviewing records in her fashion shop',
    products: ['business-finance', 'reports', 'inventory'],
    plan: 'Growth',
    staff: '6 staff',
    since: '2023',
    headline: 'How a fashion house turned its records into growth capital',
    result: '[X] days of records built',
    summary: 'Solange’s daily records became the foundation for her financing application.',
    quote: 'My records in Kayko showed how my shop really performs.',
    results: [
      { value: '[X]', label: 'days of business records' },
      { value: '[X]', label: 'shops managed in one workspace' },
      { value: '[X]%', label: 'growth in monthly sales' },
    ],
    challenge: [
      'Solange wanted to buy stock for a second shop but had no formal records to show a lender.',
    ],
    solution: [
      'Every sale and expense went into Kayko. After 90 days, she could see her eligibility checklist and apply with consent to share her records.',
    ],
    outcome: [
      'Solange now runs two shops from one workspace and has a clear, growing record of her business.',
    ],
    inlineImage: {
      src: `${IMG}d825f78d-1c2e-4866-ad21-178ec126a998.jpg`,
      alt: 'Reviewing business records with an adviser',
      caption: 'Solange’s records made her application simple.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'ring',
          title: 'Business record',
          pct: 86,
          pctLabel: '86%',
          checks: ['90+ days of active records', 'Sales recorded weekly', 'Expenses up to date'],
          action: 'Check eligibility',
        },
      },
      caption: 'The business record checklist showed Solange what to complete next.',
    },
  },
]
